/**
 * Cómo muestra el sitio público los DOS precios de un vehículo (ticket #171).
 *
 * Un vehículo con descuento tiene dos números: el precio de lista (`price`) y
 * el precio con descuento (`price` menos `discount_percentage`). Hasta este
 * ticket el sitio los dibujaba de una sola manera: el precio con descuento
 * grande, el de lista tachado y una etiqueta "-N%".
 *
 * Hay automotoras (Icardi, la que lo pidió) que no cargan un descuento sino un
 * PRECIO FINANCIADO: en Chile el precio con crédito suele ser más bajo que el
 * contado, porque la financiera paga una comisión. Para ellas el "-4%" tachado
 * le miente al cliente final: no es una rebaja, son dos formas de pago.
 *
 * ── La regla ────────────────────────────────────────────────────────────────
 *
 * La elige la AUTOMOTORA desde el Builder, en `clients.web_price_mode`:
 *
 *     'descuento'               → lo de siempre: con descuento grande, lista
 *                                  tachada y "-N%". Es el DEFAULT.
 *     'contado_financiamiento'  → "Precio financiamiento" = el precio con
 *                                  descuento, "Precio contado" = el de lista,
 *                                  cada uno con su rótulo, sin tachar y sin
 *                                  "-N%".
 *
 * Lo que NO cambia con el modo: qué número se calcula (es el mismo descuento
 * de siempre), el orden del catálogo (`precioVisible`), el buscador, ni los
 * portales. Un vehículo SIN descuento se ve igual en los dos modos: un solo
 * precio, sin rótulo.
 */

/** Lo que la automotora configura. Espejo de `clients.web_price_mode`. */
export type PrecioWebModo = 'descuento' | 'contado_financiamiento';

/** El comportamiento histórico, y el que vale si la columna todavía no existe. */
export const PRECIO_WEB_POR_DEFECTO: PrecioWebModo = 'descuento';

/**
 * Normaliza lo que venga de la fila del tenant. `undefined` es el caso real
 * mientras la migración 20261223120000 no esté aplicada: ahí vale el histórico.
 * Cualquier otro valor (null, vacío, basura) también cae al histórico.
 */
export const precioModoDelTenant = (valor: unknown): PrecioWebModo =>
  valor === 'contado_financiamiento' ? 'contado_financiamiento' : PRECIO_WEB_POR_DEFECTO;

/**
 * Lo mismo, leído de la fila del cliente tal como viaja por el sitio (el store,
 * el `initialClient` del layout, o la fila del tenant en el servidor). El tipo
 * `Client` no declara la columna a propósito —llega por el `select *`—, así que
 * se lee sin tipar, igual que `web_vehicle_order`.
 */
export const precioModoDelCliente = (client: unknown): PrecioWebModo =>
  precioModoDelTenant((client as Record<string, unknown> | null | undefined)?.web_price_mode);
